/**
 * General Ledger (Phase 1) — Finance and Admin only, behind the ledger /
 * gl_accounts capabilities. Five jobs in four tabs:
 *   Chart import · Accounts · Ledger (trial balance + account detail + manual
 *   journal entry) · Opening balance.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { BookOpen, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { glAPI } from '../lib/api';

const money = (c) => (c == null ? '—' : `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

function ChartImportTab() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const analyze = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await glAPI.chartAnalyze(fd);
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the file'); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await glAPI.chartCommit(preview.rows);
      toast.success(`Loaded ${data.loaded}, updated ${data.updated}, skipped ${data.skipped}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  };
  const s = preview?.summary;
  return (
    <div className="space-y-4" data-testid="gl-chart-import">
      <div className="flex items-center gap-3">
        <input type="file" accept=".xlsx" data-testid="chart-file-input"
          onChange={(e) => analyze(e.target.files?.[0])} />
        {busy && <span className="text-sm text-fg-muted">Working…</span>}
      </div>
      {s && (
        <>
          <div className="flex flex-wrap gap-2 text-sm" data-testid="chart-summary">
            <Badge variant="outline">{s.total} rows</Badge>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600">{s.loadable} loadable</Badge>
            <Badge variant="outline" className={s.with_problems ? 'bg-red-500/10 text-red-600' : ''}>{s.with_problems} problems</Badge>
            <Badge variant="outline" className="bg-amber-500/10 text-amber-600">{s.with_fixes} fixes applied</Badge>
            {Object.entries(s.by_type).map(([t, n]) => <Badge key={t} variant="outline">{t}: {n}</Badge>)}
          </div>
          <div className="rounded-lg border border-line overflow-auto max-h-[55vh]">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken text-fg-muted sticky top-0">
                <tr><th className="text-left px-3 py-2">GL</th><th className="text-left px-3 py-2">WinTeam</th>
                  <th className="text-left px-3 py-2">Name</th><th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Normal</th><th className="text-left px-3 py-2">Fixes / Problems</th></tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.number} className="border-t border-line" data-testid="chart-row">
                    <td className="px-3 py-1.5 tabular-nums">{r.number}</td>
                    <td className="px-3 py-1.5 tabular-nums">{r.winteam_number}</td>
                    <td className="px-3 py-1.5">{r.name}</td>
                    <td className="px-3 py-1.5">{r.type}{r.contra ? ' (contra)' : ''}</td>
                    <td className="px-3 py-1.5">{r.normal_balance}</td>
                    <td className="px-3 py-1.5 text-xs">
                      {r.problems.map((p, i) => <div key={i} className="text-red-600">⚠ {p}</div>)}
                      {r.fixes.map((f, i) => <div key={i} className="text-amber-600">✎ {f}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button onClick={commit} disabled={busy || !s.loadable} data-testid="chart-commit-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Load {s.loadable} accounts into preview
          </Button>
        </>
      )}
    </div>
  );
}

function AccountsTab() {
  const [accounts, setAccounts] = useState([]);
  const [q, setQ] = useState('');
  const load = () => glAPI.listAccounts({ search: q || undefined })
    .then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const toggle = async (a, field, value) => {
    try { await glAPI.updateAccount(a.number, { [field]: value }); toast.success('Saved'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  return (
    <div className="space-y-3" data-testid="gl-accounts">
      <div className="flex gap-2">
        <Input placeholder="Search number or name" value={q} onChange={(e) => setQ(e.target.value)}
          data-testid="accounts-search" className="max-w-xs" />
        <Button variant="outline" onClick={load} data-testid="accounts-search-btn">Search</Button>
      </div>
      <div className="rounded-lg border border-line overflow-auto max-h-[60vh]">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-fg-muted sticky top-0">
            <tr><th className="text-left px-3 py-2">Number</th><th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th><th className="text-left px-3 py-2">WinTeam</th>
              <th className="text-center px-3 py-2">Job-required</th><th className="text-center px-3 py-2">Active</th></tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.number} className="border-t border-line" data-testid="account-row">
                <td className="px-3 py-1.5 tabular-nums">{a.number}</td>
                <td className="px-3 py-1.5">{a.name}</td>
                <td className="px-3 py-1.5">{a.type}{a.contra ? ' (contra)' : ''}</td>
                <td className="px-3 py-1.5 tabular-nums">{a.winteam_number || '—'}</td>
                <td className="px-3 py-1.5 text-center">
                  <Checkbox checked={!!a.job_required} data-testid={`job-required-${a.number}`}
                    onCheckedChange={(v) => toggle(a, 'job_required', Boolean(v))} />
                </td>
                <td className="px-3 py-1.5 text-center">
                  <Checkbox checked={!!a.active} data-testid={`active-${a.number}`}
                    onCheckedChange={(v) => toggle(a, 'active', Boolean(v))} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function TrialBalanceTab() {
  const [period, setPeriod] = useState('');
  const [tb, setTb] = useState(null);
  const run = () => glAPI.trialBalance({ period_code: period || undefined })
    .then(({ data }) => setTb(data)).catch((e) => toast.error(e?.response?.data?.detail || 'Failed'));
  return (
    <div className="space-y-3" data-testid="gl-trial-balance">
      <div className="flex gap-2 items-end">
        <div><label className="text-xs text-fg-muted block mb-1">Period (YYYY-MM)</label>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="2027-01"
            data-testid="tb-period" className="max-w-[160px]" /></div>
        <Button variant="outline" onClick={run} data-testid="tb-run-btn">Trial balance</Button>
      </div>
      {tb && (
        <>
          <div className="text-sm" data-testid="tb-totals">
            Debits {money(tb.total_debits_cents)} · Credits {money(tb.total_credits_cents)} ·{' '}
            {tb.balanced ? <span className="text-emerald-600">balanced</span>
              : <span className="text-red-600">off by {money(Math.abs(tb.difference_cents))}</span>}
          </div>
          <div className="rounded-lg border border-line overflow-auto max-h-[55vh]">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken text-fg-muted sticky top-0">
                <tr><th className="text-left px-3 py-2">Account</th><th className="text-left px-3 py-2">Name</th>
                  <th className="text-right px-3 py-2">Opening</th><th className="text-right px-3 py-2">Debits</th>
                  <th className="text-right px-3 py-2">Credits</th><th className="text-right px-3 py-2">Closing</th></tr>
              </thead>
              <tbody>
                {tb.rows.map((r) => (
                  <tr key={r.account_number} className="border-t border-line" data-testid="tb-row">
                    <td className="px-3 py-1.5 tabular-nums">{r.account_number}</td>
                    <td className="px-3 py-1.5">{r.account_name}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(r.opening_cents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(r.debits_cents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(r.credits_cents)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(r.closing_cents)}</td>
                  </tr>
                ))}
                {!tb.rows.length && <tr><td colSpan={6} className="px-3 py-4 text-center text-fg-muted">No activity.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function JournalEntriesTab() {
  return <ManualEntry onPosted={() => {}} />;
}

function ManualEntry({ onPosted }) {
  const empty = () => [{ account_number: '', debit_cents: '', credit_cents: '' },
    { account_number: '', debit_cents: '', credit_cents: '' }];
  const [date, setDate] = useState('');
  const [desc, setDesc] = useState('');
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState(empty());
  const [busy, setBusy] = useState(false);
  const post = async () => {
    if (!reason.trim()) { toast.error('A reason is required'); return; }
    setBusy(true);
    try {
      await glAPI.postEntry({ entry_date: date, description: desc, reason: reason.trim(),
        lines: lines.filter((l) => l.account_number).map((l) => ({
          account_number: l.account_number,
          debit_cents: Math.round(Number(l.debit_cents || 0) * 100),
          credit_cents: Math.round(Number(l.credit_cents || 0) * 100) })) });
      toast.success('Journal entry posted');
      setLines(empty()); setDesc(''); setReason('');
      onPosted?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not post'));
    } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-line p-4 space-y-3" data-testid="gl-manual-entry">
      <div className="text-sm font-medium">Manual journal entry</div>
      <div className="flex gap-2">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="je-date" className="max-w-[160px]" />
        <Input placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="je-desc" />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="flex gap-2">
          <Input placeholder="Account #" value={l.account_number} data-testid={`je-acct-${i}`}
            onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, account_number: e.target.value } : x))} className="max-w-[140px]" />
          <Input type="number" placeholder="Debit $" value={l.debit_cents} data-testid={`je-debit-${i}`}
            onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, debit_cents: e.target.value } : x))} className="max-w-[120px]" />
          <Input type="number" placeholder="Credit $" value={l.credit_cents} data-testid={`je-credit-${i}`}
            onChange={(e) => setLines((ls) => ls.map((x, j) => j === i ? { ...x, credit_cents: e.target.value } : x))} className="max-w-[120px]" />
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={() => setLines((ls) => [...ls, { account_number: '', debit_cents: '', credit_cents: '' }])}>+ line</Button>
      <Textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="je-reason" />
      <Button onClick={post} disabled={busy} data-testid="je-post-btn">{busy ? 'Posting…' : 'Post entry'}</Button>
    </div>
  );
}

function OpeningBalanceTab() {
  const [text, setText] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [busy, setBusy] = useState(false);
  const parse = () => text.trim().split('\n').map((ln) => {
    const [acct, d, c] = ln.split(',').map((x) => x.trim());
    return { account_number: acct, debit_cents: Math.round(Number(d || 0) * 100),
      credit_cents: Math.round(Number(c || 0) * 100) };
  }).filter((l) => l.account_number);
  const analyze = async () => {
    try { const { data } = await glAPI.openingAnalyze(parse()); setAnalysis(data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const commit = async () => {
    setBusy(true);
    try {
      await glAPI.openingCommit({ lines: parse(),
        plug_to_retained_earnings: !analysis.balanced,
        entry_date: '2026-12-31', reason: 'Opening balances 12/31/2026' });
      toast.success('Opening balances posted (dated 12/31/2026)');
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not post'));
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="gl-opening-balance">
      <p className="text-sm text-fg-muted">
        Paste the 12/31/2026 trial balance as <code>account, debit, credit</code> per line. An
        unbalanced file is refused; the difference is plugged to 3900 Retained Earnings, shown
        before you confirm.
      </p>
      <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={'1000, 5000, 0\n2000, 0, 3000'} data-testid="opening-input" className="font-mono text-sm" />
      <Button variant="outline" onClick={analyze} data-testid="opening-analyze-btn">Check balance</Button>
      {analysis && (
        <div className="rounded-lg border border-line p-4 space-y-2" data-testid="opening-analysis">
          <div className="text-sm">Debits {money(analysis.total_debits_cents)} · Credits {money(analysis.total_credits_cents)}</div>
          {analysis.balanced ? (
            <div className="text-sm text-emerald-600 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4" /> Balanced.
            </div>
          ) : (
            <div className="text-sm text-amber-600 flex items-center gap-1.5" data-testid="opening-plug">
              <AlertTriangle className="h-4 w-4" />
              Off by {money(Math.abs(analysis.difference_cents))} — will plug a {analysis.plug.side} of{' '}
              {money(analysis.plug.amount_cents)} to 3900 Retained Earnings.
            </div>
          )}
          <Button onClick={commit} disabled={busy} data-testid="opening-commit-btn">
            {busy ? 'Posting…' : (analysis.balanced ? 'Post opening balances' : 'Plug to 3900 and post')}
          </Button>
        </div>
      )}
    </div>
  );
}

const GL_TAB_CONTENT = {
  accounts: AccountsTab,
  'trial-balance': TrialBalanceTab,
  'journal-entries': JournalEntriesTab,
  'chart-import': ChartImportTab,
  'opening-balances': OpeningBalanceTab,
};

export default function GeneralLedger({ tab = 'accounts' }) {
  const Content = GL_TAB_CONTENT[tab] || AccountsTab;
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="general-ledger-page">
      <div className="flex items-center gap-3">
        <BookOpen className="h-6 w-6 text-indigo-600" />
        <div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-fg tracking-tight">General Ledger</h1>
          <p className="text-sm text-fg-muted mt-1.5">Chart of accounts, ledger and opening balances — Finance and Admin.</p>
        </div>
      </div>
      <Content />
    </div>
  );
}
